import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';
import {
  useBedCountConfig,
  useUpdateBedCountConfig,
  type BedCountConfig,
  type BedCountProtocol,
  DEFAULT_BED_COUNT_CONFIG,
  SA_DEFAULT_TIMES,
  FWC_DEFAULT_TIMES,
  normalizeBedCountTime,
} from '@/hooks/useBedCountConfig';

interface BedCountSettingsProps {
  siteId: string;
}

export const BedCountSettings = ({ siteId }: BedCountSettingsProps) => {
  const { data, isLoading } = useBedCountConfig(siteId);
  const updateMut = useUpdateBedCountConfig();
  const [config, setConfig] = useState<BedCountConfig>(DEFAULT_BED_COUNT_CONFIG);
  const [newTime, setNewTime] = useState('');

  useEffect(() => {
    if (data?.config) setConfig(data.config);
  }, [data?.config]);

  const setProtocol = (p: BedCountProtocol) => {
    setConfig((prev) => ({
      ...prev,
      protocol: p,
      curfew: p === 'FWC' ? '23:00' : '22:00',
    }));
  };

  const applyDefaultTimes = () => {
    setConfig((prev) => ({
      ...prev,
      times: prev.protocol === 'FWC' ? FWC_DEFAULT_TIMES : SA_DEFAULT_TIMES,
    }));
  };

  const addTime = () => {
    const normalized = normalizeBedCountTime(newTime);
    if (!normalized) {
      toast.error('Enter a valid 24-hour time, such as 22:00');
      return;
    }
    if (config.times.includes(normalized)) return;
    setConfig({ ...config, times: [...config.times, normalized].sort() });
    setNewTime('');
  };

  const removeTime = (t: string) => {
    setConfig({ ...config, times: config.times.filter((x) => x !== t) });
  };

  const save = () => {
    const normalizedTimes = Array.from(new Set(config.times.map(normalizeBedCountTime).filter(Boolean) as string[])).sort();
    if (normalizedTimes.length !== config.times.length || !normalizeBedCountTime(config.curfew)) {
      toast.error('Review schedule times and curfew. Use valid HH:MM values only.');
      return;
    }
    updateMut.mutate(
      { siteId, config: { ...config, curfew: normalizeBedCountTime(config.curfew)!, times: normalizedTimes } },
      {
        onSuccess: () => toast.success('Bed count protocol saved'),
        onError: (e: any) => toast.error(e.message || 'Failed to save'),
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bed Count Schedule</CardTitle>
        <CardDescription>
          Configure manual count times, protocol, curfew, and identifiers for this facility.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base">Enable Bed Counts</Label>
            <p className="text-sm text-muted-foreground">When off, no scheduled counts are generated.</p>
          </div>
          <Switch
            checked={config.enabled}
            onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Protocol</Label>
            <Select value={config.protocol} onValueChange={(v) => setProtocol(v as BedCountProtocol)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="SA">Single Adult (10p / 12a / 2a)</SelectItem>
                <SelectItem value="FWC">Adult Family (11p single count)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Curfew (24h)</Label>
            <Input
              value={config.curfew}
              onChange={(e) => setConfig({ ...config, curfew: e.target.value })}
              placeholder="HH:MM"
            />
          </div>

          <div className="space-y-2">
            <Label>Facility Code</Label>
            <Input
              value={config.facility_code}
              onChange={(e) => setConfig({ ...config, facility_code: e.target.value })}
              placeholder="e.g. BOH4"
            />
          </div>

          <div className="space-y-2">
            <Label>Offline Beds</Label>
            <Input
              type="number"
              min={0}
              value={config.offline_beds}
              onChange={(e) => setConfig({ ...config, offline_beds: Number(e.target.value) || 0 })}
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label>Scheduled Times (24h)</Label>
            <Button type="button" variant="outline" size="sm" onClick={applyDefaultTimes}>
              Use {config.protocol === 'FWC' ? 'Family' : 'Single Adult'} Defaults
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {config.times.map((t) => (
              <Badge key={t} variant="secondary" className="gap-1 pr-1">
                {t}
                <button
                  onClick={() => removeTime(t)}
                  className="ml-1 hover:bg-muted rounded-sm p-0.5"
                  aria-label={`Remove ${t}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {config.times.length === 0 && (
              <span className="text-sm text-muted-foreground">No times scheduled</span>
            )}
          </div>
          <div className="flex gap-2 mt-2">
            <Input
              placeholder="HH:MM"
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
              className="max-w-32"
            />
            <Button variant="outline" size="sm" onClick={addTime}>
              <Plus className="h-4 w-4" /> Add Time
            </Button>
          </div>
        </div>

        <Button onClick={save} disabled={updateMut.isPending || isLoading}>
          {updateMut.isPending ? 'Saving…' : 'Save Settings'}
        </Button>
      </CardContent>
    </Card>
  );
};
